import {
  Avatar,
  Box,
  Button,
  Flex,
  IconButton,
  Textarea,
} from '@chakra-ui/react'
import {
  AttachmentIcon,
  LinkIcon,
} from '@chakra-ui/icons'
import {
  AlignLeft,
  Bold,
  Italic,
  Underline,
  Undo2,
  Redo2,
  ListOrdered,
  List,
  Link as LinkLucide,
  Smile,
  Image as ImageLucide,
} from 'lucide-react'
import { ListPlus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TemplatePicker } from './TemplatePicker'
import { useInboxStore } from '@/store/inbox'

type Props = {
  threadId: string | null
  toName?: string
  toEmail?: string
  value: string
  onChange: (value: string) => void
  onSend: (body: string) => void
}

export const Composer = ({
  threadId,
  toName = 'Jericho Rosales',
  toEmail = 'JerichoRosales1992@gmail.com',
  value,
  onChange,
  onSend,
}: Props) => {
  const initialsName = useMemo(() => 'AS', [])
  const isDisabled = !threadId

  const templates = useInboxStore((s) => s.templates)

  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const [taHeight, setTaHeight] = useState<number>(36) 

  const MAX_TEXTAREA_HEIGHT = 220 
  const MIN_TEXTAREA_HEIGHT = 36

  const resizeTextarea = () => {
    const el = taRef.current
    if (!el) return

    el.style.height = '0px'
    const next = Math.max(
      MIN_TEXTAREA_HEIGHT,
      Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)
    )
    el.style.height = `${next}px`
    setTaHeight(next)
  }

  useEffect(() => {
    resizeTextarea()
  }, [value, threadId])

  const handleSend = () => {
    if (!threadId) return
    const trimmed = value.trim()
    if (!trimmed) return

    onSend(trimmed)
    onChange('')
    requestAnimationFrame(() => resizeTextarea())
  }

  const insertTemplate = (text: string) => {
    const next = value?.trim() ? `${value}\n\n${text}` : text
    onChange(next)
  }

  const reachedMax = taHeight >= MAX_TEXTAREA_HEIGHT

  return (
    <Flex gap="12px" align="flex-start">
      <Avatar name={initialsName} size="sm" bg="green.500" color="white" />

      <Box
        flex="1"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="12px"
        overflow="hidden"
        bg="white"
        boxShadow="sm"
      >
        {/* header */}
        <Flex
          px="18px"
          py="12px"
          borderBottom="1px solid"
          borderColor="gray.100"
          align="center"
          justify="space-between"
          gap="12px"
        >
          <Flex align="center" gap="10px" minW="0">
            <Box fontWeight="700">To:</Box>
            <Box fontWeight="700">{toName}</Box>
            <Box color="gray.400" fontSize="sm" noOfLines={1}>
              {toEmail}
            </Box>
          </Flex>
        </Flex>

        {/* body */}
        <Box px="18px" py="10px">
          <Textarea
            ref={taRef}
            variant="unstyled"
            placeholder={isDisabled ? 'Select a thread to reply…' : 'Type a message…'}
            resize="none"
            isDisabled={isDisabled}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            h={`${taHeight}px`}
            overflowY={reachedMax ? 'auto' : 'hidden'}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
          />
        </Box>
        {/* icons not working yet*/}
        <Flex
          px="18px"
          py="10px"
          borderTop="1px solid"
          borderColor="gray.100"
          align="center"
          justify="space-between"
          gap="12px"
        >
          <Flex gap="6px" align="center" flexWrap="wrap">
            <TemplatePicker
              templates={templates}
              onPick={insertTemplate}
              trigger={
                <IconButton
                  aria-label="Insert template"
                  icon={<ListPlus size={20} strokeWidth={2} />}
                  variant="ghost"
                  size="sm"
                  isDisabled={isDisabled}
                />
              }
            />
            <IconButton
              aria-label="Align left"
              icon={<AlignLeft size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Bold"
              icon={<Bold size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Italic"
              icon={<Italic size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Underline"
              icon={<Underline size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Undo"
              icon={<Undo2 size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Redo"
              icon={<Redo2 size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Numbered list"
              icon={<ListOrdered size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Bulleted list"
              icon={<List size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Insert link (format)"
              icon={<LinkLucide size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Emoji"
              icon={<Smile size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Insert image"
              icon={<ImageLucide size={18} />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />

            {/* existing actions */}
            <IconButton
              aria-label="Attach file"
              icon={<AttachmentIcon />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
            <IconButton
              aria-label="Insert link"
              icon={<LinkIcon />}
              variant="ghost"
              size="sm"
              isDisabled={isDisabled}
            />
          </Flex>

          <Button
            colorScheme="blue"
            borderRadius="8px"
            onClick={handleSend}
            isDisabled={isDisabled || !value.trim()}
          >
            Send
          </Button>
        </Flex>
      </Box>
    </Flex>
  )
}